import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui';
import type { AssetServiceUsage } from '@/types/merchant-asset';

interface ThemeShareWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Services that reference the shared stylesheet asset (incl. this one). */
  usedBy: AssetServiceUsage[];
  /** True when the usage derivation was unavailable — wording stays conservative. */
  usageUnavailable?: boolean;
  /** Confirm and save: create a new asset for this service only (others untouched). */
  onConfirm: () => void;
  isPending?: boolean;
}

/**
 * Informational notice shown before saving when the service's stylesheet asset is
 * shared by more than one service (Issue #341 / #417). Every save now creates a NEW
 * asset and relinks only this service, so there is no in-place mutation to guard
 * against — the dialog just tells the operator that the other services keep their
 * current stylesheet. Confirming proceeds with the (create-new) save.
 */
export function ThemeShareWarningDialog({
  open,
  onOpenChange,
  usedBy,
  usageUnavailable,
  onConfirm,
  isPending,
}: ThemeShareWarningDialogProps) {
  const { t } = useTranslation('service-theme');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            {t('shareWarning.title')}
          </DialogTitle>
          <DialogDescription>
            {usageUnavailable
              ? t('shareWarning.introUnknown')
              : t('shareWarning.intro', { count: usedBy.length })}
          </DialogDescription>
        </DialogHeader>

        {!usageUnavailable && usedBy.length > 0 && (
          <div className="rounded-md border bg-muted/30 p-3">
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              {t('shareWarning.affected')}
            </p>
            <ul className="space-y-0.5 text-sm">
              {usedBy.map((u) => (
                <li key={u.serviceId}>{u.displayName}</li>
              ))}
            </ul>
          </div>
        )}

        <p className="text-sm text-muted-foreground">{t('shareWarning.effect')}</p>

        {/* One-button informational dialog (Issue #417): just acknowledge and save.
            Backing out is the dialog's built-in close (X / Escape) — there is no
            in-place mutation to cancel, every save simply creates a new asset. */}
        <DialogFooter>
          <Button type="button" onClick={onConfirm} disabled={isPending}>
            {t('shareWarning.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
